// import { useMemo } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import { Box, Chip, useMediaQuery } from '@mui/material';

import Profile from './Profile';
import Notification from './Notification';
import Settings from './Settings';
import Support from './Support';
// import LiveChat from './LiveChat';
import useAuth from '../../../../hooks/useAuth';
import { PlatformStatus, PlatformStatusService } from '../../../../services';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crisp } from 'crisp-sdk-web';
import { config } from '../../../../env';
import { v4 as uuidv4 } from 'uuid';
import GlobalSearch from '../../../../components/GlobalSearch';
import { useUpgradeDrawer } from '../../../../contexts/UpgradeDrawerContext';

// ==============================|| HEADER - CONTENT ||============================== //

const platformStatusService = new PlatformStatusService();

// Module-level cache to persist data across component remounts (e.g., theme changes)
const defaultPlatformStatus: PlatformStatus = {
  activeStatus: {
    value: 'DEFAULT',
    color: '',
    textColor: '',
    url: ''
  },
  activeMaintenances: {
    value: 'DEFAULT',
    color: '',
    textColor: '',
    url: '',
    title: ''
  },
  activeIncidents: {
    value: 'DEFAULT',
    color: '',
    textColor: '',
    url: '',
    title: ''
  }
};
let cachedHeaderStatus: PlatformStatus = defaultPlatformStatus;
let hasFetchedHeaderStatus = false;

const HeaderContent = () => {
  const { user, refreshToken, accessToken } = useAuth();
  const { openUpgradeDrawer } = useUpgradeDrawer();
  // const { i18n } = useConfig();
  const [active, setActive] = useState<PlatformStatus>(cachedHeaderStatus);

  const matchesXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Prevent re-fetching on theme change remounts
    if (hasFetchedHeaderStatus) return;
    hasFetchedHeaderStatus = true;

    platformStatusService
      .getStatus(accessToken || null)
      .then((res) => {
        if (isMounted.current) {
          cachedHeaderStatus = res;
          setActive(res);
        }
      })
      .catch((err) => {
        console.error('ERROR: ', err);
        if (isMounted.current) {
          setActive(defaultPlatformStatus);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      platformStatusService
        .getStatus(accessToken || null)
        .then((res) => {
          if (isMounted.current) setActive(res);
        })
        .catch((err) => {
          console.error('ERROR: ', err);
          if (isMounted.current) {
            setActive({
              activeStatus: {
                value: 'DEFAULT',
                color: '',
                textColor: '',
                url: ''
              },
              activeMaintenances: {
                value: 'DEFAULT',
                color: '',
                textColor: '',
                url: '',
                title: ''
              },
              activeIncidents: {
                value: 'DEFAULT',
                color: '',
                textColor: '',
                url: '',
                title: ''
              }
            });
          }
        });
    }, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const [read, setRead] = useState(0);

  useEffect(() => {
    if (!user || !user?.tcId) {
      refreshToken()
        .then(() => {
          if (user?.tcId) {
            Crisp.configure(config.crisp_website_id, {
              sessionMerge: true,
              tokenId: user?.tcId
            });
            Crisp.setTokenId(user?.tcId || uuidv4());
          } else {
            Crisp.configure(config.crisp_website_id);
          }
          Crisp.setSafeMode(true);
          Crisp.user.setEmail(user?.email || '');
          Crisp.user.setNickname(user?.name || '');
          Crisp.chat.hide();
          Crisp.setSafeMode(true);
          setTimeout(() => {
            if (isMounted.current) {
              setRead(Crisp.chat.unreadCount() || 0);
            }
          }, 4000);
        })
        .catch((err) => {
          console.error('ERROR: ', err);
        });
    } else {
      if (user?.tcId) {
        Crisp.configure(config.crisp_website_id, {
          sessionMerge: true,
          tokenId: user?.tcId
        });
        Crisp.setTokenId(user?.tcId || uuidv4());
      } else {
        Crisp.configure(config.crisp_website_id);
      }
      Crisp.setSafeMode(true);
      Crisp.user.setEmail(user?.email || '');
      Crisp.user.setNickname(user?.name || '');
      Crisp.chat.hide();
      Crisp.setSafeMode(true);
      setTimeout(() => {
        if (isMounted.current) {
          setRead(Crisp.chat.unreadCount() || 0);
        }
      }, 4000);
    }
  }, [user]);

  Crisp?.message?.onMessageReceived(() => {
    if (!isMounted.current) return;
    if (Crisp.chat.isVisible()) {
      setRead(0);
    } else {
      setRead(Crisp?.chat?.unreadCount());
    }
  });

  return (
    <>
      {!matchesXs && <GlobalSearch />}
      {user?.isBetaUser && <Chip sx={{ ml: 2, borderRadius: '12px' }} label="Beta" size="small" color="primary" />}
      {!user?.roles?.includes('Admin') && !user?.roles?.includes('Paid Member') && !user?.roles?.includes('Overdue Subscription') && (
        <Chip
          sx={{ ml: 2, borderRadius: '12px' }}
          onClick={() => openUpgradeDrawer()}
          label="Upgrade Now"
          size="small"
          color="success"
        />
      )}
      {!user?.roles?.includes('Admin') && !user?.roles?.includes('Paid Member') && user?.roles?.includes('Overdue Subscription') && (
        <Chip
          sx={{ ml: 2, borderRadius: '12px' }}
          onClick={() => navigate('/account-settings/billing?openModal=true', { replace: false })}
          label="Subscription overdue - Make payment in billing"
          size="small"
          color="error"
        />
      )}
      <>
        {(user?.roles?.includes('Admin') || user?.roles?.includes('Paid Member')) &&
          active?.activeStatus?.value !== 'DEFAULT' &&
          active?.activeStatus?.value !== 'UP' &&
          active?.activeIncidents?.value === 'DEFAULT' && (
            <Chip
              sx={{ ml: 2, borderRadius: '12px' }}
              label={active?.activeStatus?.value}
              size="small"
              style={{
                background: active?.activeStatus?.color,
                cursor: 'pointer',
                color: active?.activeStatus?.textColor
              }}
              onClick={() => window.open(active.activeStatus?.url)}
            />
          )}
        {(user?.roles?.includes('Admin') || user?.roles?.includes('Paid Member')) && active?.activeIncidents?.value !== 'DEFAULT' && (
          <Chip
            sx={{ ml: 2, borderRadius: '12px' }}
            label={
              active?.activeIncidents?.title
                ? active?.activeIncidents?.value + ': ' + active?.activeIncidents?.title
                : active?.activeIncidents?.value
            }
            size="small"
            style={{
              background: active?.activeIncidents?.color,
              cursor: 'pointer',
              color: active?.activeIncidents?.textColor
            }}
            onClick={() => window.open(active.activeIncidents?.url)}
          />
        )}
        {(user?.roles?.includes('Admin') || user?.roles?.includes('Paid Member')) && active?.activeMaintenances?.value !== 'DEFAULT' && (
          <Chip
            sx={{ ml: 2, borderRadius: '12px' }}
            label={
              active?.activeMaintenances?.title
                ? active?.activeMaintenances?.value + ': ' + active?.activeMaintenances?.title
                : active?.activeMaintenances?.value
            }
            size="small"
            style={{
              background: active?.activeMaintenances?.color,
              cursor: 'pointer',
              color: active?.activeMaintenances?.textColor
            }}
            onClick={() => window.open(active.activeMaintenances?.url)}
          />
        )}
      </>

      <Box sx={{ width: '100%', ml: { xs: 0, md: 1 } }} />
      {matchesXs && <Box sx={{ width: '100%', ml: 1 }} />}
      {read > 0 && <Notification readCount={read} handleRead={(count) => setRead(count)} />}
      <Settings />
      <Support />
      {/* <LiveChat /> */}
      {!matchesXs && <Box sx={{ width: '1px', height: 24, bgcolor: 'divider', mx: 1 }} />}
      <Profile />
    </>
  );
};

export default HeaderContent;
